import React, { useState, useRef, useCallback, useEffect } from "react";
import style from "./OrdersChatHub.module.scss";
import { XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import useOrderChats from "../../../../hooks/useOrderChats";
import ChatListPanel from "./components/ChatListPanel";
import ChatWindow from "./components/ChatWindow";

const MAX_CHAT_FILE_SIZE_BYTES = 20 * 1024 * 1024;

const OrdersChatHub = ({ isOpen, orderData, orderId, orderNumber: orderNumberProp, onClose, serviceType = "order" }) => {
  const { t } = useTranslation();
  const messagesEndRef = useRef(null);
  const [messageText, setMessageText] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const {
    chats,
    selectedChat,
    setSelectedChat,
    loading,
    orderNumber: fetchedOrderNumber,
    sendMessage,
    canSendMessage,
    chatFiles,
    isChatFilesLoading,
    downloadChatFile,
  } = useOrderChats({
    isOpen,
    orderId,
    orderData,
    t,
    serviceType,
  });

  const orderNumber = orderNumberProp || fetchedOrderNumber;

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [selectedChat, scrollToBottom]);

  useEffect(() => {
    setMessageText("");
  }, [selectedChat?.id]);

  const handleFileSelect = useCallback(
    async (files) => {
      const file = files?.[0];
      if (!file || isUploading) return;

      if (file.size > MAX_CHAT_FILE_SIZE_BYTES) {
        toast.error(t("orderChat.fileSizeLimit"));
        return;
      }

      setIsUploading(true);
      try {
        await sendMessage("", [file]);
        setTimeout(scrollToBottom, 50);
      } finally {
        setIsUploading(false);
      }
    },
    [sendMessage, scrollToBottom, isUploading, t],
  );

  const handleSendMessage = useCallback(async () => {
    if (!messageText.trim()) return;
    setIsUploading(true);
    try {
      const success = await sendMessage(messageText, []);
      if (success) {
        setMessageText("");
        setTimeout(scrollToBottom, 50);
      }
    } finally {
      setIsUploading(false);
    }
  }, [messageText, sendMessage, scrollToBottom]);

  const handleOverlayClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  if (!isOpen) return null;

  return (
    <div className={style.modal_overlay} onClick={handleOverlayClick}>
      <div className={style.modal_container}>
        <div className={style.modal_header}>
          <h2 className={style.modal_title}>
            {t("orderChat.orderMessages")} №{orderNumber}
          </h2>
          <button className={style.close_button} onClick={onClose}>
            <XIcon size={20} color="#6A7282" />
          </button>
        </div>

        <div className={style.chat_content}>
          <ChatListPanel
            chats={chats}
            selectedChat={selectedChat}
            onSelectChat={setSelectedChat}
            loading={loading}
            loadingText={`${t("orderChat.loadingChats")}...`}
            emptyText={t("orderChat.noMessagesForOrder")}
          />

          <ChatWindow
            selectedChat={selectedChat}
            messageText={messageText}
            onMessageChange={setMessageText}
            onSendMessage={handleSendMessage}
            placeholder={t("orderChat.writeMessagePlaceholder")}
            t={t}
            messagesEndRef={messagesEndRef}
            canSendMessage={canSendMessage}
            disabledMessage={t("orderChat.canOnlyMessageExpeditor")}
            chatFiles={chatFiles}
            isChatFilesLoading={isChatFilesLoading}
            onDownloadFile={downloadChatFile}
            onFileSelect={handleFileSelect}
            isUploading={isUploading}
          />
        </div>
      </div>
    </div>
  );
};

export default OrdersChatHub;
